import React from 'react';
import { format } from 'date-fns';
import type { LucideIcon } from 'lucide-react';
import { ActivityIcon, Clock, Users, Wrench, MoreVertical, Pencil, Trash2, ClipboardList } from 'lucide-react';
import { Button } from "@/modern/components/ui/button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/modern/components/ui/tooltip";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/modern/components/ui/dropdown-menu";
import { cn } from "@/modern/utils";
import { useTimelineContext } from '../context/TimelineContext';
import { extractInitials } from '../utils/transformers';

const EVENT_META: Record<
  string,
  {
    label: string;
    icon: LucideIcon;
    accent: string;
    surface: string;
    border: string;
    indicator: string;
  }
> = {
  status: {
    label: "Status updates",
    icon: ActivityIcon,
    accent: "text-emerald-600 dark:text-emerald-300",
    surface: "bg-emerald-600/10",
    border: "border-emerald-600/20",
    indicator: "bg-emerald-600"
  },
  schedule: {
    label: "Scheduled resources",
    icon: Users,
    accent: "text-sky-600 dark:text-sky-300",
    surface: "bg-sky-600/10",
    border: "border-sky-600/20",
    indicator: "bg-sky-600"
  },
  planning: {
    label: "Planning milestones",
    icon: Wrench,
    accent: "text-amber-600 dark:text-amber-300",
    surface: "bg-amber-600/10",
    border: "border-amber-600/20",
    indicator: "bg-amber-600"
  },
  reminder: {
    label: "Reminders",
    icon: Clock,
    accent: "text-purple-600 dark:text-purple-300",
    surface: "bg-purple-600/10",
    border: "border-purple-600/20",
    indicator: "bg-purple-600"
  },
  history: {
    label: "Task history",
    icon: ClipboardList,
    accent: "text-slate-600 dark:text-slate-300",
    surface: "bg-slate-600/10",
    border: "border-slate-600/20",
    indicator: "bg-slate-600"
  }
};

interface TimelineEventCardProps {
  event: Record<string, unknown>;
  type: string;
}

const formatDisplayName = (name: string): string => name;

const formatTimelineDate = (value?: string) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return format(parsed, "MMM d");
};

export const TimelineEventCard: React.FC<TimelineEventCardProps> = ({ event, type }) => {
  const {
    handleStatusClick,
    handleScheduleResourceClick,
    handlePlanningEventClick,
    handleReminderClick,
    handleHistoryClick,
    handleEditReminder,
    handleDeleteReminder,
    handleEditSchedule,
    handleDeleteSchedule,
    handleEditPlanning,
    handleDeletePlanning
  } = useTimelineContext();

  const meta = EVENT_META[type] ?? EVENT_META.reminder;
  const IconComponent = meta.icon;

  // Status card
  if (type === "status") {
    return (
      <button
        type="button"
        onClick={() => handleStatusClick({
          label: event.label as string,
          date: event.date as string,
          updatedBy: event.updatedBy as string
        })}
        className="group flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <div className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded", meta.surface)}>
          <IconComponent className={cn("h-3 w-3", meta.accent)} />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-medium text-foreground truncate">{event.label as string}</p>
          {event.updatedBy ? <p className="mt-0.5 text-xs text-muted-foreground truncate">by {event.updatedBy as string}</p> : null}
        </div>
      </button>
    );
  }

  // Schedule card
  if (type === "schedule") {
    const resources = ((event.resources as string[]) || []).map((r) => formatDisplayName(r));
    const count = resources.length;
    const originalEvents = (event.originalEvents as Record<string, unknown>[]) || [event];
    const eventId = originalEvents[0]?.id as string;

    return (
      <div className="group relative flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition hover:bg-muted/30">
        <button
          type="button"
          onClick={() => handleScheduleResourceClick(originalEvents)}
          className="flex-1 flex items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded min-w-0"
        >
          <div className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded", meta.surface)}>
            <IconComponent className={cn("h-3 w-3", meta.accent)} />
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <p className="text-sm font-medium text-foreground truncate min-w-0">
              {count} resource{count === 1 ? "" : "s"} scheduled
            </p>
            {count > 0 && (
              <TooltipProvider>
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-1.5">
                    {resources.slice(0, 3).map((resource, resourceIdx) => (
                      <Tooltip key={resourceIdx}>
                        <TooltipTrigger asChild>
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-background text-[10px] font-semibold uppercase ring-1 ring-border/60">
                            {extractInitials(resource)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{resource}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    {count > 3 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-1 ring-border/60">+{count - 3}</div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{resources.slice(3).join(", ")}</p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {resources.slice(0, 2).join(", ")}
                    {count > 2 ? ` +${count - 2} more` : ""}
                  </span>
                </div>
              </TooltipProvider>
            )}
          </div>
        </button>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditSchedule(eventId);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteSchedule(eventId);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  // Planning card
  if (type === "planning") {
    const startLabel = formatTimelineDate(event.startDate as string);
    const endLabel = formatTimelineDate(event.endDate as string);
    const rangeLabel = [startLabel, endLabel].filter(Boolean).join(" – ");
    const durationLabel = (event.duration as string) || "";
    const showDuration = durationLabel && !durationLabel.toLowerCase().includes("not available");
    const planningId = event.id as string;

    return (
      <div className="group relative flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition hover:bg-muted/30">
        <button
          type="button"
          onClick={() => handlePlanningEventClick({
            label: event.label as string,
            date: event.date as string,
            color: event.color as string,
            startDate: event.startDate as string,
            endDate: event.endDate as string,
            duration: event.duration as string
          })}
          className="flex-1 flex items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded min-w-0"
        >
          <div className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded", meta.surface)}>
            <IconComponent className={cn("h-3 w-3", meta.accent)} />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 min-w-0">
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: (event.color as string) || "#facc15" }} />
              <p className="text-sm font-medium text-foreground truncate min-w-0">{event.label as string}</p>
            </div>
            {(rangeLabel || showDuration) && (
              <p className="mt-0.5 text-xs text-muted-foreground truncate min-w-0">
                {rangeLabel}
                {showDuration && rangeLabel && <span className="mx-1">·</span>}
                {showDuration && durationLabel}
              </p>
            )}
          </div>
        </button>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditPlanning(planningId);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeletePlanning(planningId);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  // Reminder card
  if (type === "reminder") {
    const notifyUsers = (event.notifies as string[]) || [];
    const recurrence = (event.recurrence as string) || "";
    const reminderId = event.id as string;

    return (
      <div className="group relative flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition hover:bg-muted/30">
        <button
          type="button"
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          onClick={() => handleReminderClick(event.reminder as any)}
          className="flex-1 flex items-start gap-3 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30 rounded pr-8 min-w-0"
        >
          <div className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded", meta.surface)}>
            <IconComponent className={cn("h-3 w-3", meta.accent)} />
          </div>
          <div className="flex-1 min-w-0 space-y-1.5">
            <div className="flex items-center gap-2 min-w-0">
              <p className="text-sm font-medium text-foreground truncate min-w-0 flex-1">{event.label as string}</p>
              {recurrence && recurrence !== "Never" && <span className="text-[10px] font-medium text-muted-foreground shrink-0">{recurrence}</span>}
            </div>
            {notifyUsers.length > 0 && (
              <TooltipProvider>
                <div className="flex items-center gap-1.5">
                  <div className="flex -space-x-1.5">
                    {notifyUsers.slice(0, 3).map((user, userIdx) => (
                      <Tooltip key={userIdx}>
                        <TooltipTrigger asChild>
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-background text-[10px] font-semibold uppercase ring-1 ring-border/60">
                            {extractInitials(user)}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>{formatDisplayName(user)}</p>
                        </TooltipContent>
                      </Tooltip>
                    ))}
                    {notifyUsers.length > 3 && (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <div className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-[10px] font-medium text-muted-foreground ring-1 ring-border/60">
                            +{notifyUsers.length - 3}
                          </div>
                        </TooltipTrigger>
                        <TooltipContent>
                          <p>
                            {notifyUsers
                              .slice(3)
                              .map((u) => formatDisplayName(u))
                              .join(", ")}
                          </p>
                        </TooltipContent>
                      </Tooltip>
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">{notifyUsers.length} notified</span>
                </div>
              </TooltipProvider>
            )}
          </div>
        </button>
        <div className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="icon" variant="ghost" className="h-7 w-7" onClick={(e) => e.stopPropagation()}>
                <MoreVertical className="h-4 w-4" />
                <span className="sr-only">Actions</span>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleEditReminder(reminderId);
                }}
              >
                <Pencil className="h-4 w-4 mr-2" />
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={(e) => {
                  e.stopPropagation();
                  handleDeleteReminder(reminderId);
                }}
                className="text-destructive focus:text-destructive"
              >
                <Trash2 className="h-4 w-4 mr-2" />
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>
    );
  }

  // History card
  if (type === "history") {
    return (
      <button
        type="button"
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        onClick={() => handleHistoryClick(event as any)}
        className="group flex w-full items-start gap-3 rounded-md border px-3 py-2.5 text-left transition hover:bg-muted/30 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
      >
        <div className={cn("mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded", meta.surface)}>
          <IconComponent className={cn("h-3 w-3", meta.accent)} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 min-w-0">
            {event.taskColor ? (
              <span className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: event.taskColor as string }} />
            ) : null}
            <p className="text-sm font-medium text-foreground truncate min-w-0">{event.taskName as string}</p>
          </div>
          <p className="mt-0.5 text-xs text-muted-foreground truncate min-w-0">
            {event.duration as string}
            {event.status ? <span className="ml-2">· {event.status as string}</span> : null}
          </p>
        </div>
      </button>
    );
  }

  return null;
};
